import React, { useRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mergeRefs from "merge-refs";
import { twJoin, twMerge } from "tailwind-merge";

import { Tooltip } from "src/design-system/Tooltip";
import { FIELD_BORDER, FIELD_TEXT } from "src/design-system/utils/fieldStyles";

export type InputIconProps = {
  icon: IconProp;
};

type InputProps = Omit<
  React.ComponentPropsWithoutRef<"input">,
  "style" | "className"
> & {
  dataLoc?: string;
  variant?: "default" | "monospaced";
  error?: boolean;
  errorMessage?: string;
  hinted?: boolean;
  highlighted?: boolean;
  prefixIcon?: InputIconProps;
  suffixIcon?: InputIconProps;
  prefixText?: string;
  suffixText?: string;
  ref?: React.Ref<HTMLInputElement>;
};

export const Input: React.FC<InputProps> = ({
  dataLoc,
  variant = "default",
  error = false,
  errorMessage,
  hinted = false,
  highlighted = false,
  prefixIcon,
  suffixIcon,
  prefixText,
  suffixText,
  disabled = false,
  ref,
  ...inputProps
}) => {
  const innerRef = useRef<HTMLInputElement>(null);

  const isMonospaced = variant === "monospaced";
  const hasPrefixText = isMonospaced && !!prefixText;
  const showPrefixIcon = prefixIcon && !hasPrefixText;
  const showErrorIcon = error && !!errorMessage;
  const showSuffixIcon = !showErrorIcon && !!suffixIcon;

  const borderStyle = highlighted
    ? "border-yellow-400 bg-yellow-50"
    : error
      ? FIELD_BORDER.errored
      : hinted
        ? FIELD_BORDER.hinted
        : FIELD_BORDER.default;

  const textColorStyle = disabled
    ? undefined
    : isMonospaced
      ? prefixText
        ? "text-green-600"
        : "text-indigo-600"
      : hinted && !error
        ? FIELD_TEXT.hinted
        : FIELD_TEXT.default;

  return (
    <div
      className={twMerge(
        "flex h-8 w-full items-center overflow-hidden rounded-lg border font-inter-normal-12px",
        disabled ? "bg-gray-50 text-gray-500" : "cursor-text bg-white",
        borderStyle,
        !error &&
          !highlighted &&
          "focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-600/25",
        isMonospaced && "font-mono",
        textColorStyle,
      )}
      onClick={() => innerRef.current?.focus()}
    >
      {showPrefixIcon && (
        <FontAwesomeIcon
          className={twJoin(
            "flex-shrink-0 pl-4",
            error ? "text-red-400" : "text-gray-500",
          )}
          icon={prefixIcon.icon}
          width={12}
        />
      )}
      {hasPrefixText && (
        <div className="flex h-full items-center border-gray-200 border-r bg-gray-100 pr-2 pl-3 font-code-12 text-indigo-500">
          {prefixText}
        </div>
      )}
      <input
        ref={mergeRefs(ref, innerRef)}
        autoComplete="off"
        className={twJoin(
          "min-w-0 flex-1 py-[5px] pr-2 outline-none placeholder:text-gray-500",
          "no-number-spinner",
          disabled && !highlighted && "bg-gray-50",
          highlighted && "bg-transparent",
          isMonospaced
            ? "font-code-12 placeholder:font-code-12"
            : "font-inter-normal-12px placeholder:font-inter-normal-12px",
          !showPrefixIcon && !hasPrefixText ? "pl-3" : "pl-2",
        )}
        data-loc={dataLoc}
        disabled={disabled}
        {...inputProps}
      />
      {suffixText && (
        <div className="flex h-full items-center border-gray-200 border-l bg-gray-100 pr-3 pl-2 font-inter-normal-12px text-gray-500">
          {suffixText}
        </div>
      )}
      {showErrorIcon && (
        <Tooltip body={errorMessage} placement="top" asChild>
          <FontAwesomeIcon
            className="mr-3 flex-shrink-0 text-red-400"
            icon={faWarning}
            width={12}
          />
        </Tooltip>
      )}
      {showSuffixIcon && (
        <FontAwesomeIcon
          className={twJoin(
            "mr-3 flex-shrink-0",
            error ? "text-red-400" : "text-gray-500",
          )}
          icon={suffixIcon.icon}
          width={12}
        />
      )}
    </div>
  );
};
